import moment from 'moment-timezone';
import { Discontinued, Refused } from '../frequencies/frequency-constants';
import { dateToMoment, momentToDate } from '../dates';
import { formatPostDate } from '../formatters';
import { LOC, FC, PRN, CPF } from './orders-constants';
export const isDiscontinued = freq => freq === Discontinued || freq === Refused;
/**
 * communicationdatetime for progress-note saves (formatPostDate, like modifieddatetime).
 * Non-order → null. Physician order → now. Non-physician order → verbalDate
 * (already offsetDate'd so the calendar day matches site TZ).
 */

export function getCommunicationDateTime(isOrder, isPhysicianRoleGroup, verbalDate) {
  if (!isOrder) {
    return null;
  }

  if (isPhysicianRoleGroup) {
    return formatPostDate(new Date());
  }

  return verbalDate ? formatPostDate(verbalDate) : null;
}
/* 
  Our date pickers return Date objects with user timezones appended to the end. We assume
  users are picking their dates in the site's timezone, so this is usually incorrect. This function 
  outputs a Date object with the right timezone.

  For example, if I'm in EST and select 11/13 as my date, the picker will return a Date
  like: "Wed Nov 13 2024 00:00:00 GMT-0500 (Eastern Standard Time)". However the site is in CST.
  Running offsetDate on this object will return "Wed Nov 13 2024 01:00:00 GMT-0500 (Eastern Standard Time)."
*/

export function offsetDate(date) {
  return dateToMoment(date).toDate();
}
/*
  Even though the output from offsetDate is an accurate date-time we can't feed it back to
  the picker to display, because it's in the user time zone. Unfortunately the Date object
  can't handle changing its timezone string. The solution here is to change the time part
  of the string and leave the timezone part intact. We are "lying" to the picker to get
  it to display in site tz.

  For example the user picked "Wed Nov 13 2024 00:00:00 GMT-0500 (Eastern Standard Time)"
  which actually gives "Wed Nov 13 2024 01:00:00 GMT-0500 (Eastern Standard Time)" when
  run through offsetDate and stored. We can't give this back to the picker, so toSiteTz 
  gives us "Wed Nov 13 2024 00:00:00 GMT-0500 (Eastern Standard Time)" again.
*/

export function toSiteTz(date, siteTimezone) {
  return momentToDate(moment.tz(date, moment.tz.guess()).tz(siteTimezone));
}
/**
 * Verbal Date for LOC and FC orders have add requirements than basic date constraint OLD LOGIC
 */
// NOT USED

export const getVerbalDate = (patientDateRestrictions, orderType) => {
  const isLOCOrder = orderType === LOC;
  const isFCOrder = orderType === FC;
  const basicDateConstraint = patientDateRestrictions === null || patientDateRestrictions === void 0 ? void 0 : patientDateRestrictions.basicDateConstraint;
  let verbalDate = basicDateConstraint;

  if (isLOCOrder || isFCOrder) {
    if (isLOCOrder) {
      const lastLOCDate = patientDateRestrictions === null || patientDateRestrictions === void 0 ? void 0 : patientDateRestrictions.lastloc;
      verbalDate = moment.max(moment(basicDateConstraint), moment(lastLOCDate)).startOf('day');
      return verbalDate;
    }

    if (isFCOrder) {
      const lastFCDate = patientDateRestrictions === null || patientDateRestrictions === void 0 ? void 0 : patientDateRestrictions.lastfac;
      verbalDate = moment.max(moment(basicDateConstraint), moment(lastFCDate)).startOf('day');
      return verbalDate;
    }
  } // start of day for future comparisons


  return moment(verbalDate).startOf('day');
};
export const getMaxOrderDate = (patientDateRestrictions, orderType) => {
  const isLOCOrder = orderType === LOC;
  const isFCOrder = orderType === FC;
  const basicDateConstraint = patientDateRestrictions === null || patientDateRestrictions === void 0 ? void 0 : patientDateRestrictions.basicDateConstraint;
  const maxFutureDate = moment(Date.now()).add(3, 'days');
  let orderDate = basicDateConstraint;
  let orderDateError = false;

  if (isLOCOrder || isFCOrder) {
    const prevOrderDate = isLOCOrder ? patientDateRestrictions === null || patientDateRestrictions === void 0 ? void 0 : patientDateRestrictions.lastloc : patientDateRestrictions === null || patientDateRestrictions === void 0 ? void 0 : patientDateRestrictions.lastfac; // if pimreader maxdate is NOT in the future, use 3 days in future for order maximum date

    if (!moment(prevOrderDate).isAfter(moment())) {
      orderDate = maxFutureDate;
    } // If maxdate is IN the future and it's < 3 days in future, show a  message "LOC/Location change already scheduled for {pimreader maxdate}"


    if (moment(prevOrderDate).isAfter(moment()) && moment(prevOrderDate).isBefore(maxFutureDate)) {
      orderDate = maxFutureDate;
      orderDateError = prevOrderDate;
    } // ORDER maximum date to earlier of pimreader maxdate, 3 days in future


    if (moment(prevOrderDate).isAfter(maxFutureDate)) {
      orderDate = moment(maxFutureDate);
    }

    return {
      orderDate: orderDate.startOf('day'),
      orderDateError
    };
  } // start of day for future comparisons


  return {
    orderDate: orderDate.startOf('day'),
    orderDateError
  };
};
export const getMinVerbalDate = (referralDate, patientDateRestrictions, orderType) => {
  const isLOCOrder = orderType === LOC;
  const isFCOrder = orderType === FC;
  const basicDateConstraint = patientDateRestrictions === null || patientDateRestrictions === void 0 ? void 0 : patientDateRestrictions.basicDateConstraint; // referralDate is the MIN date for VERBAL DATE for LOC and LC

  let verbalDate = basicDateConstraint;

  if (isLOCOrder || isFCOrder) {
    if (referralDate) {
      verbalDate = referralDate;
    }
  }

  let verbalDateMin = moment(verbalDate).startOf('day');

  if (moment.isMoment(verbalDateMin)) {
    verbalDateMin = verbalDateMin.toDate();
  } // start of day for future comparisons


  return verbalDateMin;
};
export const getUserCredentials = user => {
  const {
    firstName,
    lastName,
    userCredentials
  } = user;
  const userTitle = userCredentials ? `${firstName} ${lastName}, ${userCredentials}` : `${firstName} ${lastName}`;
  return userTitle;
};
export const getLocalOrderTime = user => {
  const tz = moment.tz.guess();
  const siteTimezone = user ? user.siteTimezone : tz;
  const tzDate = moment.utc(new Date()).tz(siteTimezone).format('MM/DD/YYYY HH:mm:ss');
  return tzDate;
};
export const verifyFreqEditingData = VForderData => {
  const cpvfList = VForderData && Object.values(VForderData);
  const isVerified = cpvfList && (cpvfList === null || cpvfList === void 0 ? void 0 : cpvfList.length) > 0 && !!Object.values(cpvfList).every(freq => freq.effectiveDate && freq.period);
  return isVerified;
};
export const verifySingleFreqEditingData = freq => freq.effectiveDate && freq.period;
export const verifySingleFreqPRNEditingData = freq => freq.effectiveDate;
export const isFreqOrder = type => type === PRN || type === CPF;